import { useEffect, useRef } from 'react';
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Shield,
  Target,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  BarChart2,
  Layers,
  Activity,
  ArrowUpRight,
  Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROUTE_PATHS } from "@/lib/index";
import { Layout } from "@/components/Layout";

// ─── Motion Variants ──────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6 } },
};
const stagger = (delay = 0.1) => ({
  visible: { transition: { staggerChildren: delay } },
  hidden: {},
});

// ─── Animated Section Wrapper ────────────────────────────────────────────────
function Reveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{ hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] } } }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── Results Preview Card ─────────────────────────────────────────────────────
function ResultsPreviewCard() {
  return (
    <div className="relative w-full">
      {/* Outer glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-accent/20 via-primary/10 to-transparent rounded-3xl blur-2xl" />

      <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200/80 overflow-hidden">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400/60" />
            <div className="w-3 h-3 rounded-full bg-amber-400/60" />
            <div className="w-3 h-3 rounded-full bg-emerald-400/60" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="text-[11px] text-slate-400 font-mono bg-slate-100 rounded px-3 py-0.5">salesforge.de/ergebnisse</div>
          </div>
        </div>

        {/* Header bar */}
        <div className="bg-slate-900 px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Sales Audit · Ergebnisse</div>
              <div className="text-white font-bold text-sm">B2B Software GmbH · 8 Reps</div>
            </div>
            <div className="flex items-center gap-1.5 text-amber-400 text-[11px] font-semibold">
              <AlertCircle className="w-3.5 h-3.5" />
              Kritisch
            </div>
          </div>
          {/* KPI row */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Monatliche Lücke", value: "38.400 €", color: "text-red-400" },
              { label: "Größter Hebel", value: "Conversion", color: "text-amber-400" },
              { label: "Kapazität vs. Ziel", value: "64%", color: "text-slate-300" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-white/5 rounded-lg px-3 py-2">
                <div className="text-[9px] text-slate-400 mb-0.5">{kpi.label}</div>
                <div className={`text-sm font-bold ${kpi.color}`}>{kpi.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Content area */}
        <div className="px-5 py-4 space-y-3 bg-white">
          {/* Business Reality */}
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 font-semibold">Business Reality</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Aktueller Monatsumsatz", val: "86.160 €", sub: "bei 8 aktiven Reps" },
                { label: "Forecast Jahresende", val: "1.033.920 €", sub: "Ziel: 1.400.000 €", alert: true },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg p-3 border ${item.alert ? "border-red-200 bg-red-50" : "border-slate-100 bg-slate-50"}`}>
                  <div className="text-[9px] text-slate-500 mb-1">{item.label}</div>
                  <div className={`font-bold text-sm ${item.alert ? "text-red-600" : "text-slate-800"}`}>{item.val}</div>
                  <div className="text-[9px] text-slate-400 mt-0.5">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Structural causes */}
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 font-semibold">Strukturelle Ursachen</div>
            <div className="space-y-1.5">
              {[
                { label: "Conversion: Präsentation → Abschluss", val: "14%", bench: "28%", crit: true },
                { label: "Ø Kontakte pro Rep/Monat", val: "47", bench: "80+", crit: true },
                { label: "Sales Cycle Länge", val: "4,5 Mon.", bench: "2–3 Mon.", crit: false },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.crit ? "bg-red-400" : "bg-amber-400"}`} />
                    <span className="text-[10px] text-slate-600 truncate">{row.label}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className={`text-[11px] font-bold ${row.crit ? "text-red-600" : "text-amber-600"}`}>{row.val}</span>
                    <span className="text-[9px] text-slate-400">vs. {row.bench}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Focus fields */}
          <div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 font-semibold">Top Fokusfelder</div>
            <div className="flex flex-wrap gap-1.5">
              {["Abschlussquote steigern", "Kontaktfrequenz erhöhen", "Pipeline-Qualität", "Rep Steuerung"].map((tag, i) => (
                <div
                  key={tag}
                  className={`rounded-full px-2.5 py-1 text-[9px] font-semibold ${
                    i === 0 ? "bg-red-100 text-red-700" : i === 1 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {i === 0 ? "1." : i === 1 ? "2." : ""} {tag}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA bar */}
        <div className="px-5 py-3 bg-slate-900 flex items-center justify-between">
          <div className="text-[10px] text-slate-400">Ihr vollständiger Audit wartet</div>
          <div className="flex items-center gap-1 text-accent text-[11px] font-bold">
            Handlungsempfehlung ansehen <ArrowUpRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── System Diagram ─────────────────────────────────────────────────────────
function SystemDiagram() {
  const nodes = [
    { id: "pipeline", label: "Pipeline", desc: "Volumen & Qualität", icon: Layers, status: "ok" },
    { id: "conversion", label: "Conversion", desc: "Abschlussquote", icon: Target, status: "critical" },
    { id: "capacity", label: "Kapazität", desc: "Team vs. Ziel", icon: Activity, status: "strained" },
    { id: "forecast", label: "Forecast", desc: "Jahresabweichung", icon: BarChart2, status: "critical" },
  ];

  const statusColor = {
    ok: "border-emerald-300 bg-emerald-50 text-emerald-700",
    strained: "border-amber-300 bg-amber-50 text-amber-700",
    critical: "border-red-300 bg-red-50 text-red-700",
  };
  const statusDot = { ok: "bg-emerald-400", strained: "bg-amber-400", critical: "bg-red-400" };

  return (
    <div className="relative">
      {/* Central connector */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-16 h-16 rounded-full bg-primary/8 border-2 border-primary/15 flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-primary/40" />
        </div>
      </div>
      {/* Connector lines */}
      <div className="absolute inset-0 pointer-events-none">
        <svg width="100%" height="100%" className="absolute inset-0">
          <line x1="50%" y1="50%" x2="20%" y2="20%" stroke="currentColor" strokeWidth="1" className="text-slate-200" strokeDasharray="4 4" />
          <line x1="50%" y1="50%" x2="80%" y2="20%" stroke="currentColor" strokeWidth="1" className="text-slate-200" strokeDasharray="4 4" />
          <line x1="50%" y1="50%" x2="20%" y2="80%" stroke="currentColor" strokeWidth="1" className="text-slate-200" strokeDasharray="4 4" />
          <line x1="50%" y1="50%" x2="80%" y2="80%" stroke="currentColor" strokeWidth="2" className="text-red-300" strokeDasharray="0" />
        </svg>
      </div>

      <div className="grid grid-cols-2 gap-4 relative z-10">
        {nodes.map((node, i) => {
          const Icon = node.icon;
          return (
            <motion.div
              key={node.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className={`rounded-xl border-2 p-4 ${statusColor[node.status as keyof typeof statusColor]}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  <div className={`w-2 h-2 rounded-full ${statusDot[node.status as keyof typeof statusDot]}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="font-bold text-sm">{node.label}</span>
                  </div>
                  <div className="text-xs opacity-75">{node.desc}</div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Annotation */}
      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <div className="w-3 h-0.5 bg-red-400" />
        <span className="text-xs">Ein Engpass beeinflusst das gesamte System</span>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Layout>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1 — HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[94vh] flex items-center overflow-hidden bg-slate-950">
        {/* Background layers */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          {/* Radial gradient base */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_0%,rgba(30,60,120,0.5),transparent)]" />
          {/* Subtle grid */}
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px" }}
          />
          {/* Accent orbs */}
          <div className="absolute top-24 left-1/4 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 lg:px-8 relative z-10 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">

            {/* LEFT: Copy */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger(0.12)}
            >
              {/* Eyebrow */}
              <motion.div variants={fadeUp} className="mb-8">
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-slate-400 border border-slate-700 rounded-full px-4 py-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  B2B Sales Diagnose
                </span>
              </motion.div>

              {/* Headline */}
              <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight text-white mb-6">
                Warum erreicht Ihr Vertrieb das{" "}
                <span className="relative">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-emerald-400 to-accent">Umsatzziel</span>
                </span>{" "}
                nicht?
              </motion.h1>

              {/* Subline */}
              <motion.p variants={fadeUp} className="text-lg text-slate-300 mb-10 leading-relaxed max-w-xl">
                Salesforge analysiert Ihre Vertriebsperformance und zeigt, welcher Hebel Ihr Wachstum aktuell wirklich blockiert.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-12">
                <Button
                  asChild
                  size="lg"
                  className="h-14 px-8 text-base font-bold bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/25 transition-all duration-300 rounded-xl"
                >
                  <Link to={ROUTE_PATHS.AUDIT} className="flex items-center gap-2">
                    Kostenloses Audit starten
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </Button>
                <a
                  href="#preview"
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Beispiel-Ergebnis ansehen
                  <ChevronRight className="w-4 h-4" />
                </a>
              </motion.div>

              {/* Trust signals */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-6 text-sm text-slate-500">
                {[
                  { icon: Clock, text: "ca. 15 Minuten" },
                  { icon: Shield, text: "Kein Sales-Gespräch" },
                  { icon: CheckCircle, text: "Sofortige Auswertung" },
                ].map(({ icon: Icon, text }) => (
                  <span key={text} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-slate-500" />
                    {text}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* RIGHT: Results Preview */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block"
            >
              <ResultsPreviewCard />
            </motion.div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2 — DAS EIGENTLICHE PROBLEM
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <Reveal className="max-w-2xl mb-20">
            <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">Das eigentliche Problem</div>
            <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight text-foreground">
              Die meisten Vertriebsteams optimieren am falschen Hebel.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Layers,
                number: "01",
                title: "Die Pipeline wirkt voll — das Umsatzziel wird trotzdem verfehlt.",
                body: "Volumen ist kein Indikator für Ergebnis. Wenn Conversion-Raten strukturell zu niedrig sind, hilft mehr Pipeline nicht weiter. Das Problem liegt tiefer.",
              },
              {
                icon: Activity,
                number: "02",
                title: "Top Performer liefern konstant — der Rest des Teams nicht.",
                body: "Wenn Ergebnisse an Einzelpersonen hängen, ist das System nicht skalierbar. Der Abstand zwischen Best und Average kostet jeden Monat Umsatz.",
              },
              {
                icon: BarChart2,
                number: "03",
                title: "Forecasts sehen gut aus — Abschlüsse bleiben aus.",
                body: "Forecast und Realität klaffen auseinander — das ist ein Qualitätsproblem in der Pipeline, kein Momentum-Problem. Ursache und Symptom werden verwechselt.",
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="group h-full border border-border rounded-2xl p-8 hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-card">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/6 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary/60" />
                    </div>
                    <div className="text-4xl font-black text-muted-foreground/15 font-mono">{item.number}</div>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3 leading-snug">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3 — SO FUNKTIONIERT SALESFORGE
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 bg-slate-50" id="wie-es-funktioniert">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <Reveal className="text-center max-w-2xl mx-auto mb-20">
            <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">Einfach. Strukturiert. Wirksam.</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-foreground">So funktioniert Salesforge</h2>
          </Reveal>

          {/* Steps — horizontal flow */}
          <div className="relative">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-14 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "01",
                  label: "Audit ausfüllen",
                  title: "Aktuelle Vertriebsperformance erfassen",
                  body: "Gezielte Fragen zu Team, Funnel, Conversion und Steuerung. In ca. 15 Minuten — keine IT, keine Datenexporte.",
                  tags: ["Akquise", "Conversion", "Pipeline", "Steuerung"],
                  time: "15 Min.",
                },
                {
                  step: "02",
                  label: "Diagnose erhalten",
                  title: "Engpässe & Umsatzlücken sichtbar machen",
                  body: "Sofortige Auswertung: Wo verliert Ihr System Umsatz? Welcher Hebel hat den stärksten Einfluss — und warum?",
                  tags: ["Umsatzlücke", "Strukturelle Ursachen", "Größter Hebel"],
                  time: "Sofort",
                },
                {
                  step: "03",
                  label: "Plan ableiten",
                  title: "Priorisierter Maßnahmenplan für 90 Tage",
                  body: "Klare Stoßrichtungen, Reihenfolge und Management-Empfehlungen. Kein Aktionismus — strukturierter Fokus.",
                  tags: ["Prioritäten", "30/60/90 Tage", "Rep Execution"],
                  time: "Heute",
                },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 0.12}>
                  <div className="relative flex flex-col">
                    {/* Step number with circle */}
                    <div className="relative z-10 flex items-center justify-center w-14 h-14 rounded-full bg-background border-2 border-primary text-primary font-black text-sm mb-8 shadow-md">
                      {item.step}
                    </div>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{item.label}</div>
                    <h3 className="text-xl font-bold text-foreground mb-3 leading-snug">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-5">{item.body}</p>
                    <div className="mt-auto flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span key={tag} className="text-[11px] font-medium bg-primary/6 text-primary/80 rounded-full px-2.5 py-0.5">{tag}</span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      {item.time}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4 — SIGNATURE INSIGHT
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 bg-slate-950 text-white overflow-hidden">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-20 items-center">

            {/* Left: Copy */}
            <Reveal>
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-6">Systemdiagnose</div>
              <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight mb-6">
                Die meisten Teams lösen{" "}
                <span className="text-amber-400">Symptome</span>{" "}
                — nicht den eigentlichen Engpass.
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Ein einziger schwacher Punkt im Vertriebssystem bremst das gesamte Ergebnis. Mehr Leads helfen nicht, wenn die Conversion gebrochen ist. Mehr Training hilft nicht, wenn die Pipeline-Qualität das eigentliche Problem ist.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed mb-10">
                Salesforge macht sichtbar, wo das System strukturell unter Leistung liegt — und welcher Eingriff den größten Hebel freisetzt.
              </p>
              <Link
                to={ROUTE_PATHS.AUDIT}
                className="inline-flex items-center gap-2 text-accent font-semibold hover:gap-3 transition-all"
              >
                Meinen Engpass identifizieren <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>

            {/* Right: System Diagram */}
            <Reveal delay={0.2}>
              <SystemDiagram />
            </Reveal>
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 5 — PREVIEW DER ERGEBNISSEITE
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 bg-background" id="preview">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">Was Sie erhalten</div>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-foreground mb-4">
              Keine Theorie. Ihre Zahlen.
            </h2>
            <p className="text-lg text-muted-foreground">
              Ihre Ergebnisseite liefert eine vollständige Diagnose — aufgebaut wie ein Management-Report, nicht wie ein Dashboard.
            </p>
          </Reveal>

          {/* Large preview card */}
          <Reveal delay={0.1}>
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-950 rounded-3xl overflow-hidden shadow-2xl border border-slate-800">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[11px] text-slate-400 uppercase tracking-widest mb-1">Salesforge · Sales Audit</div>
                      <div className="text-white font-bold text-lg">Vollständige Vertriebsdiagnose</div>
                    </div>
                    <div className="flex gap-2">
                      {["Business Reality", "Ursachen", "Fokusfelder"].map((tag) => (
                        <span key={tag} className="text-[10px] font-medium text-slate-400 border border-slate-700 rounded-full px-2.5 py-1 hidden sm:block">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Sections grid */}
                <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-800">
                  {[
                    {
                      icon: TrendingUp,
                      label: "Business Reality",
                      metric: "38.400 €",
                      unit: "Monatliche Lücke",
                      items: ["Aktueller Pace vs. Ziel", "Forecast Jahresende", "Umsatz pro Rep"],
                      color: "text-red-400",
                    },
                    {
                      icon: AlertCircle,
                      label: "Strukturelle Ursachen",
                      metric: "3 kritische",
                      unit: "Engpässe erkannt",
                      items: ["Conversion P→A: 14%", "Kontaktfrequenz: –40%", "Sales Cycle: 4,5 Mon."],
                      color: "text-amber-400",
                    },
                    {
                      icon: Target,
                      label: "Fokusfelder",
                      metric: "Priorität 1",
                      unit: "Abschlussquote",
                      items: ["Klarer Fokus", "Reihenfolge definiert", "90-Tage-Struktur"],
                      color: "text-accent",
                    },
                  ].map((col) => {
                    const Icon = col.icon;
                    return (
                      <div key={col.label} className="px-6 py-8">
                        <div className="flex items-center gap-2 mb-5">
                          <Icon className={`w-4 h-4 ${col.color}`} />
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{col.label}</span>
                        </div>
                        <div className={`text-3xl font-black mb-1 ${col.color}`}>{col.metric}</div>
                        <div className="text-xs text-slate-500 mb-5">{col.unit}</div>
                        <div className="space-y-2">
                          {col.items.map((item) => (
                            <div key={item} className="flex items-center gap-2 text-[12px] text-slate-400">
                              <Minus className="w-3 h-3 text-slate-600 shrink-0" />
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Blurred bottom CTA */}
                <div className="relative">
                  <div className="px-8 py-5 blur-sm select-none pointer-events-none opacity-40 border-t border-slate-800">
                    <div className="flex gap-3">
                      {["Rep Execution Plan", "30/60/90 Maßnahmen", "Conversion-Rechner"].map((item) => (
                        <div key={item} className="flex-1 h-10 bg-slate-800 rounded-lg" />
                      ))}
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Link to={ROUTE_PATHS.AUDIT}>
                      <Button className="bg-accent text-white hover:bg-accent/90 font-semibold shadow-lg shadow-accent/30 rounded-xl px-6">
                        Vollständige Analyse starten
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 6 — VERTRAUEN / GLAUBWÜRDIGKEIT
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">Analytische Grundlage</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-foreground mb-3">
              Diagnose basiert auf realen B2B-Mustern.
            </h2>
            <p className="text-muted-foreground text-lg">
              Nicht auf Marketing-Theorie. Nicht auf generischen Checkpoints.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                metric: "14 %",
                label: "Ø Abschlussquote",
                note: "Typisch für B2B-Teams ohne strukturierte Qualifizierung. Benchmark: 22–28 %.",
              },
              {
                metric: "3,2×",
                label: "Umsatz-Spread",
                note: "Zwischen schwächstem und stärkstem Rep im selben Team. Systemisches Problem, kein Recruiting-Problem.",
              },
              {
                metric: "67 %",
                label: "Forecast-Abweichung",
                note: "Der Anteil der Teams, deren Jahres-Forecast mehr als 20 % vom Ziel abweicht.",
              },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="bg-white rounded-2xl border border-border p-8 hover:shadow-md transition-shadow">
                  <div className="text-4xl font-black text-primary mb-2">{item.metric}</div>
                  <div className="text-sm font-bold text-foreground mb-3">{item.label}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{item.note}</div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Credibility text block */}
          <Reveal>
            <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-border p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-5 h-5 text-primary/60" />
                </div>
                <div>
                  <div className="font-bold text-foreground mb-2">Benchmarkgestützte Analyse</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Salesforge vergleicht Ihre Kennzahlen mit typischen Mustern aus B2B-Vertriebsteams verschiedener Branchen und Größen.
                    Die Diagnose basiert nicht auf Durchschnittswerten, sondern auf strukturellen Mustern: Welche Kombination aus Conversion-Raten,
                    Teamkapazität und Pipeline-Qualität führt regelmäßig zu Zielverfehlungen — und welcher Eingriff wirkt am stärksten?
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 7 — HANDLUNGSEMPFEHLUNG ANTEASER
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-28 bg-background">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <Reveal>
              <div className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-6">Ebene 2</div>
              <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight text-foreground mb-6">
                Aus Analyse wird ein klarer{" "}
                <span className="text-accent">90-Tage-Plan.</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Die Handlungsempfehlung geht weiter als die Diagnose: konkrete Management-Stoßrichtungen, Reihenfolge, Fokus und eine Rep-Execution-Ebene.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Priorisierte Fokusfelder — was konkret geändert werden muss",
                  "30/60/90-Tage-Struktur mit klarer Phasenlogik",
                  "Rep Execution — was jeder Rep monatlich leisten muss",
                  "Conversion-Rechner — was passiert, wenn Sie Raten verbessern",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-accent" />
                    </div>
                    <span className="text-sm text-foreground leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground bg-muted rounded-xl px-4 py-2">
                <Shield className="w-4 h-4" />
                Freischaltbar nach dem Audit — einmalig, 149 €
              </div>
            </Reveal>

            {/* Right: Teaser card */}
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="bg-slate-900 rounded-2xl overflow-hidden border border-slate-700 shadow-xl">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                    <div className="text-white font-bold text-sm">Handlungsempfehlung</div>
                    <div className="text-[10px] text-slate-400 border border-slate-700 rounded-full px-2.5 py-1">Nach Audit verfügbar</div>
                  </div>
                  {/* Phases */}
                  <div className="px-6 py-5 space-y-3">
                    {[
                      { phase: "Phase 1 · 0–30 Tage", title: "Schnellste Hebel aktivieren", color: "text-accent" },
                      { phase: "Phase 2 · 30–60 Tage", title: "Systematische Optimierung", color: "text-blue-400" },
                      { phase: "Phase 3 · 60–90 Tage", title: "Strukturelle Verankerung", color: "text-slate-300" },
                    ].map((phase, i) => (
                      <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3">
                        <div>
                          <div className="text-[10px] text-slate-500 mb-0.5">{phase.phase}</div>
                          <div className={`text-sm font-semibold ${phase.color}`}>{phase.title}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      </div>
                    ))}
                  </div>
                  {/* Blur overlay */}
                  <div className="relative">
                    <div className="px-6 py-4 blur-sm opacity-30 select-none">
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-8 bg-white/5 rounded-lg" />
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-slate-900/80 to-transparent">
                      <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <Shield className="w-3.5 h-3.5" />
                        Detailplan nach Audit verfügbar
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>


{/* ══════════════════════════════════════════════════════════════════════
          SECTION 8 — TESTIMONIALS
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-muted-foreground border border-border rounded-full px-4 py-2 mb-6">
                <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                Was unsere Kunden sagen
              </span>
              <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tight">
                Echte Ergebnisse. Echte Unternehmen.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "Der Audit hat uns gezeigt, dass nicht das Lead-Volumen unser Problem war – sondern die Conversion vom Discovery Call zum Angebot. Wir haben gezielt dort angesetzt und die Abschlussquote innerhalb von 6 Wochen um 18 Prozentpunkte gesteigert.",
                name: "Vertriebsleiter",
                company: "B2B SaaS · 12 Reps · DACH",
                metric: "+18 %",
                metricLabel: "Abschlussquote",
              },
              {
                quote: "Wir dachten, wir brauchen mehr Leads. Salesforge hat uns in 15 Minuten bewiesen, dass wir bereits genug Leads hatten – aber 60 % davon nie ein Follow-up bekommen haben. Das war der eigentliche Hebel.",
                name: "Geschäftsführer",
                company: "Industrieausrüstung · Mittelstand · Deutschland",
                metric: "−60 %",
                metricLabel: "verlorene Follow-ups",
              },
              {
                quote: "Was mich überzeugt hat: keine generischen Tipps, sondern ein klarer Fokus auf genau die zwei Stellschrauben, die bei uns wirklich ausschlaggebend waren. Das hätte uns intern Monate gedauert herauszufinden.",
                name: "Head of Sales",
                company: "B2B Consulting · 6 Reps · Österreich",
                metric: "2 Hebel",
                metricLabel: "identifiziert in 15 min",
              },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="flex flex-col h-full rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <svg key={s} className="w-4 h-4 fill-accent text-accent" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  {/* Quote */}
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-6 italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <div className="font-semibold text-sm text-foreground">{t.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t.company}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-black text-accent">{t.metric}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{t.metricLabel}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 9 — FINAL CTA
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-32 bg-slate-950 text-white relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_100%,rgba(34,197,94,0.1),transparent)]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
        </div>

        <div className="container mx-auto px-6 lg:px-8 max-w-4xl relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger(0.1)}
            className="text-center"
          >
            <motion.div variants={fadeIn} className="mb-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-slate-400 border border-slate-700 rounded-full px-4 py-2">
                <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                Kostenfrei starten
              </span>
            </motion.div>

            <motion.h2 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6">
              Finden Sie heraus, welcher Hebel Ihren Vertrieb{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-emerald-300">aktuell wirklich ausbremst.</span>
            </motion.h2>

            <motion.p variants={fadeUp} className="text-lg text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              In 15 Minuten zur vollständigen Vertriebsdiagnose. Keine Anmeldung. Kein Sales-Gespräch. Sofortige Auswertung.
            </motion.p>

            <motion.div variants={fadeUp}>
              <Button
                asChild
                size="lg"
                className="h-16 px-12 text-lg font-bold bg-accent text-white hover:bg-accent/90 shadow-xl shadow-accent/30 transition-all duration-300 rounded-xl mb-6"
              >
                <Link to={ROUTE_PATHS.AUDIT} className="flex items-center gap-3">
                  <Target className="w-5 h-5" />
                  Kostenloses Audit starten
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={fadeIn} className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
              {[
                { icon: Clock, text: "ca. 15 Minuten" },
                { icon: Shield, text: "Kein Sales-Gespräch" },
                { icon: CheckCircle, text: "Sofortige Auswertung" },
                { icon: Target, text: "Kostenlos" },
              ].map(({ icon: Icon, text }) => (
                <span key={text} className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  {text}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

    </Layout>
  );
}
